<template>
  <div class="results-page">
    <div class="page-header">
      <h1><i class="pi pi-chart-bar"></i> Результати тестування</h1>
      <p>Переглядайте результати та аналітику тестів</p>
    </div>

    <div class="content-section">
      <div class="filters">
        <div class="filter-group">
          <label>Сесія:</label>
          <Dropdown
            v-model="selectedSession"
            :options="sessionOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Всі сесії"
            class="filter-dropdown"
          />
        </div>
        <div class="filter-group">
          <label>Тип тесту:</label>
          <Dropdown
            v-model="selectedTestType"
            :options="testTypeOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Всі типи"
            class="filter-dropdown"
          />
        </div>
        <div class="filter-group">
          <label>Період:</label>
          <Calendar
            v-model="dateRange"
            selectionMode="range"
            placeholder="Виберіть період"
            class="date-picker"
          />
        </div>
      </div>

      <div class="results-overview">
        <div class="stats-grid">
          <div class="stat-card">
            <div class="stat-icon">
              <i class="pi pi-users"></i>
            </div>
            <div class="stat-content">
              <h3>{{ totalParticipants }}</h3>
              <p>Усього учасників</p>
            </div>
          </div>
          
          <div class="stat-card">
            <div class="stat-icon">
              <i class="pi pi-check-circle"></i>
            </div>
            <div class="stat-content">
              <h3>{{ completedTests }}</h3>
              <p>Завершених тестів</p>
            </div>
          </div>
          
          <div class="stat-card">
            <div class="stat-icon">
              <i class="pi pi-percentage"></i>
            </div>
            <div class="stat-content">
              <h3>{{ completionRate }}%</h3>
              <p>Відсоток завершення</p>
            </div>
          </div>
          
          <div class="stat-card">
            <div class="stat-icon">
              <i class="pi pi-star"></i>
            </div>
            <div class="stat-content">
              <h3>{{ averageScore }}</h3>
              <p>Середній бал</p>
            </div>
          </div>
        </div>
      </div>

      <div class="results-tabs">
        <TabView>
          <TabPanel header="Мої результати">
            <div class="my-results">
              <div
                v-for="result in myResults"
                :key="result.id"
                class="result-card"
              >
                <div class="result-header">
                  <h3>{{ result.session?.name }}</h3>
                  <Tag :value="result.session?.test_type?.category" severity="info" />
                </div>
                
                <div class="result-details">
                  <div class="result-score">
                    <div class="score-item">
                      <span class="score-label">Загальний бал:</span>
                      <span class="score-value">{{ result.total_score || 'N/A' }}</span>
                    </div>
                    <div class="score-item">
                      <span class="score-label">Середній бал:</span>
                      <span class="score-value">{{ result.average_score || 'N/A' }}</span>
                    </div>
                  </div>
                  
                  <div v-if="result.enps_result" class="enps-result">
                    <h4>eNPS Результат</h4>
                    <div class="enps-score">
                      <span class="nps-score">{{ result.enps_result.main_question_score }}/10</span>
                      <Tag :value="getNPSCategoryLabel(result.enps_result.category)" :severity="getNPSSeverity(result.enps_result.category)" />
                    </div>
                    
                    <!-- Category-specific questions -->
                    <div v-if="result.enps_result.category_questions" class="category-questions">
                      <div v-for="(answer, question) in result.enps_result.category_questions" :key="question" class="question-answer">
                        <h5>{{ getQuestionLabel(question) }}</h5>
                        <p>{{ answer }}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div v-if="result.gallup_result" class="gallup-result">
                    <h4>Gallup Q12 Результат</h4>
                    <div class="engagement-score">
                      <span class="score">{{ result.gallup_result.overall_score }}</span>
                      <Tag :value="result.gallup_result.engagement_level" :severity="getEngagementSeverity(result.gallup_result.engagement_level)" />
                    </div>
                  </div>
                </div>
                
                <div class="result-footer">
                  <span class="completion-date">
                    Завершено: {{ formatDate(result.completed_at) }}
                  </span>
                  <Button
                    label="Деталі"
                    icon="pi pi-eye"
                    class="p-button-text"
                    @click="viewResultDetails(result.id)"
                  />
                </div>
              </div>
              
              <div v-if="myResults.length === 0" class="empty-state">
                <i class="pi pi-chart-line empty-icon"></i>
                <h3>Немає результатів</h3>
                <p>Ви ще не завершили жодного тесту.</p>
              </div>
            </div>
          </TabPanel>
          
          <TabPanel v-if="isAdmin" header="Загальна аналітика">
            <div class="analytics-section">
              <div class="analytics-filters">
                <div class="filter-group">
                  <label>Тип тесту:</label>
                  <Dropdown
                    v-model="selectedAnalyticsTestType"
                    :options="testTypeOptions"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Всі типи"
                    class="filter-dropdown"
                  />
                </div>
                <div class="filter-group">
                  <label>Статус сесії:</label>
                  <Dropdown
                    v-model="selectedSessionStatus"
                    :options="sessionStatusOptions"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Всі статуси"
                    class="filter-dropdown"
                  />
                </div>
              </div>

              <div class="analytics-overview">
                <div class="stats-grid">
                  <div class="stat-card">
                    <div class="stat-icon">
                      <i class="pi pi-calendar"></i>
                    </div>
                    <div class="stat-content">
                      <h3>{{ filteredSessions.length }}</h3>
                      <p>Активних сесій</p>
                    </div>
                  </div>
                  
                  <div class="stat-card">
                    <div class="stat-icon">
                      <i class="pi pi-users"></i>
                    </div>
                    <div class="stat-content">
                      <h3>{{ totalAnalyticsParticipants }}</h3>
                      <p>Усього учасників</p>
                    </div>
                  </div>
                  
                  <div class="stat-card">
                    <div class="stat-icon">
                      <i class="pi pi-check-circle"></i>
                    </div>
                    <div class="stat-content">
                      <h3>{{ totalAnalyticsCompleted }}</h3>
                      <p>Завершених тестів</p>
                    </div>
                  </div>
                  
                  <div class="stat-card">
                    <div class="stat-icon">
                      <i class="pi pi-percentage"></i>
                    </div>
                    <div class="stat-content">
                      <h3>{{ averageAnalyticsCompletionRate }}%</h3>
                      <p>Середній відсоток завершення</p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="analytics-table">
                <h3>Детальна статистика по сесіях</h3>
                <DataTable :value="filteredSessionAnalytics" :paginator="true" :rows="10" class="analytics-datatable">
                  <Column field="session.name" header="Сесія">
                    <template #body="slotProps">
                      <div class="session-info">
                        <strong>{{ slotProps.data.session.name }}</strong>
                        <Tag :value="getCategoryLabel(slotProps.data.session.test_type?.category)" severity="info" />
                      </div>
                    </template>
                  </Column>
                  <Column field="period" header="Період проведення">
                    <template #body="slotProps">
                      {{ slotProps.data.period }}
                    </template>
                  </Column>
                  <Column field="total_participants" header="Учасників">
                    <template #body="slotProps">
                      <span class="participants-count">{{ slotProps.data.total_participants }}</span>
                    </template>
                  </Column>
                  <Column field="completed_responses" header="Завершено">
                    <template #body="slotProps">
                      <span class="completed-count">{{ slotProps.data.completed_responses }}</span>
                    </template>
                  </Column>
                  <Column field="completion_rate" header="Відсоток завершення">
                    <template #body="slotProps">
                      <div class="completion-rate">
                        <ProgressBar :value="slotProps.data.completion_rate" :showValue="true" />
                      </div>
                    </template>
                  </Column>
                  <Column field="average_score" header="Середній бал">
                    <template #body="slotProps">
                      {{ calculateAverageScore(slotProps.data.session) }}
                    </template>
                  </Column>
                  <Column field="nps_score" header="eNPS">
                    <template #body="slotProps">
                      {{ calculateNPSScore(slotProps.data.session) }}
                    </template>
                  </Column>
                  <Column field="engagement_score" header="Залуженість">
                    <template #body="slotProps">
                      {{ calculateEngagementScore(slotProps.data.session) }}
                    </template>
                  </Column>
                  <Column header="Дії">
                    <template #body="slotProps">
                      <div class="action-buttons">
                        <Button
                          icon="pi pi-eye"
                          class="p-button-text p-button-sm"
                          @click="viewSessionDetails(slotProps.data.session)"
                          v-tooltip.top="'Деталі сесії'"
                        />
                        <Button
                          icon="pi pi-chart-bar"
                          class="p-button-text p-button-sm"
                          @click="viewSessionAnalytics(slotProps.data.session)"
                          v-tooltip.top="'Аналітика'"
                        />
                        <Button
                          icon="pi pi-download"
                          class="p-button-text p-button-sm"
                          @click="exportSessionResults(slotProps.data.session)"
                          v-tooltip.top="'Експорт'"
                        />
                      </div>
                    </template>
                  </Column>
                </DataTable>
              </div>
            </div>
          </TabPanel>
        </TabView>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'

export default {
  name: 'Results',
  data() {
    return {
      selectedSession: null,
      selectedTestType: null,
      dateRange: null,
      sessionOptions: [
        { label: 'Всі сесії', value: null }
      ],
      testTypeOptions: [
        { label: 'Всі типи', value: null },
        { label: 'Gallup Q12', value: 'gallup_q12' },
        { label: 'eNPS', value: 'enps' },
        { label: 'Кастомні', value: 'custom' }
             ],
       selectedAnalyticsTestType: null,
       selectedSessionStatus: null,
       sessionStatusOptions: [
         { label: 'Всі статуси', value: null },
         { label: 'Активні', value: 'active' },
         { label: 'Неактивні', value: 'inactive' }
       ]
     }
   },
  computed: {
    ...mapState(['results', 'sessions', 'currentUser']),
    ...mapGetters(['isAdmin']),
    myResults() {
      return this.results.filter(result => result.user === this.currentUser?.id)
    },
    totalParticipants() {
      return this.sessions.reduce((total, session) => total + (session.participants_count || 0), 0)
    },
    completedTests() {
      return this.results.length
    },
    completionRate() {
      if (this.totalParticipants === 0) return 0
      return Math.round((this.completedTests / this.totalParticipants) * 100)
    },
    averageScore() {
      if (this.results.length === 0) return 'N/A'
      const total = this.results.reduce((sum, result) => sum + (result.average_score || 0), 0)
      return (total / this.results.length).toFixed(2)
    },
    sessionAnalytics() {
      return this.sessions.map(session => {
        // Get participants count from backend
        const participantsCount = session.participants_count || 0
        
        // Get completed count from backend
        const completedResponses = session.completed_count || 0
        
        // Calculate completion rate
        const completionRate = participantsCount > 0 ? Math.round((completedResponses / participantsCount) * 100) : 0
        
        // Format dates for period display
        const startDate = session.start_date ? new Date(session.start_date).toLocaleDateString('uk-UA') : 'N/A'
        const endDate = session.end_date ? new Date(session.end_date).toLocaleDateString('uk-UA') : 'N/A'
        
                 return {
           session,
           total_participants: participantsCount,
           completed_responses: completedResponses,
           completion_rate: completionRate,
           period: `${startDate} - ${endDate}`
         }
       })
     },
     filteredSessions() {
       return this.sessions.filter(session => {
         if (this.selectedAnalyticsTestType && session.test_type?.category !== this.selectedAnalyticsTestType) {
           return false
         }
         if (this.selectedSessionStatus === 'active' && !session.is_active) {
           return false
         }
         if (this.selectedSessionStatus === 'inactive' && session.is_active) {
           return false
         }
         return true
       })
     },
     filteredSessionAnalytics() {
       return this.sessionAnalytics.filter(analytics => {
         return this.filteredSessions.some(session => session.id === analytics.session.id)
       })
     },
     totalAnalyticsParticipants() {
       return this.filteredSessionAnalytics.reduce((total, analytics) => total + analytics.total_participants, 0)
     },
     totalAnalyticsCompleted() {
       return this.filteredSessionAnalytics.reduce((total, analytics) => total + analytics.completed_responses, 0)
     },
     averageAnalyticsCompletionRate() {
       if (this.filteredSessionAnalytics.length === 0) return 0
       const totalRate = this.filteredSessionAnalytics.reduce((sum, analytics) => sum + analytics.completion_rate, 0)
       return Math.round(totalRate / this.filteredSessionAnalytics.length)
     }
  },
  watch: {
    results: {
      handler() {
        // Debounce chart updates to prevent multiple rapid updates
        // This logic is no longer needed as charts are removed
      },
      deep: true
    }
  },
  methods: {
    ...mapActions(['fetchResults', 'fetchSessions', 'fetchTestTypes']),
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('uk-UA')
    },
    getNPSSeverity(category) {
      const severities = {
        promoter: 'success',
        passive: 'warning',
        detractor: 'danger'
      }
      return severities[category] || 'info'
    },
    getNPSCategoryLabel(category) {
      const labels = {
        promoter: 'Промоутер (9-10)',
        passive: 'Пасивний (7-8)',
        detractor: 'Детрактор (0-6)'
      }
      return labels[category] || category
    },
    getQuestionLabel(questionKey) {
      const labels = {
        'promoter_question_1': 'Що вам найбільше подобається в роботі в Центрі?',
        'promoter_question_2': 'Що саме мотивує вас рекомендувати наш Центр як роботодавця?',
        'promoter_question_3': 'Які аспекти нашої роботи, на вашу думку, ми робимо особливо добре?',
        'passive_question_1': 'Що могло б зробити ваш досвід роботи в Центрі кращим?',
        'passive_question_2': 'Яких змін чи покращень вам не вистачає?',
        'detractor_question_1': 'Що є найбільшим джерелом розчарування у вашій роботі?',
        'detractor_question_2': 'Якби ви мали можливість змінити одну річ у Центрі, що б це було?',
        'detractor_question_3': 'Основні причини, через які ви не готові рекомендувати наш Центр?',
        'detractor_question_4': 'Які аспекти нашої роботи ми робимо не дуже добре?'
      }
      return labels[questionKey] || questionKey
    },
    getEngagementSeverity(level) {
      const severities = {
        high: 'success',
        medium: 'warning',
        low: 'danger'
      }
      return severities[level] || 'info'
    },
    calculateCompletionRate(session) {
      const participants = session.participants?.length || 0
      const completed = this.results.filter(r => r.session === session.id).length
      return participants > 0 ? Math.round((completed / participants) * 100) : 0
    },
    calculateAverageScore(session) {
      const sessionResults = this.results.filter(r => r.session === session.id)
      if (sessionResults.length === 0) return 'N/A'
      const total = sessionResults.reduce((sum, result) => sum + (result.average_score || 0), 0)
      return (total / sessionResults.length).toFixed(2)
    },
    calculateNPSScore(session) {
      const sessionResults = this.results.filter(r => r.session === session.id)
      if (sessionResults.length === 0) return 'N/A'
      
      const enpsResults = sessionResults.filter(r => r.enps_result)
      if (enpsResults.length === 0) return 'N/A'
      
      const promoters = enpsResults.filter(r => r.enps_result.category === 'promoter').length
      const detractors = enpsResults.filter(r => r.enps_result.category === 'detractor').length
      const total = enpsResults.length
      
      if (total === 0) return 'N/A'
      
      const promoterPercentage = (promoters / total) * 100
      const detractorPercentage = (detractors / total) * 100
      
      return Math.round(promoterPercentage - detractorPercentage)
    },
    calculateEngagementScore(session) {
      const sessionResults = this.results.filter(r => r.session === session.id)
      if (sessionResults.length === 0) return 'N/A'
      
      // Filter for Gallup Q12 results only
      const gallupResults = sessionResults.filter(r => r.gallup_result)
      if (gallupResults.length === 0) return 'N/A'
      
      // Calculate average engagement score from Gallup Q12 results
      const totalScore = gallupResults.reduce((sum, result) => {
        return sum + (result.gallup_result.overall_score || 0)
      }, 0)
      
      return (totalScore / gallupResults.length).toFixed(2)
    },
         viewResultDetails(resultId) {
            // Implementation for viewing result details
            console.log('Viewing result:', resultId)
        },
     viewSessionDetails(session) {
       // Navigate to session results page
       this.$router.push(`/session/${session.id}/results`)
     },
     viewSessionAnalytics(session) {
            // Implementation for viewing session analytics
            console.log('Viewing session analytics:', session)
        },
     exportSessionResults(session) {
            // Implementation for exporting session results
            console.log('Exporting session results:', session)
        },
     getCategoryLabel(category) {
       const labels = {
         gallup_q12: 'Gallup Q12',
         enps: 'eNPS',
         custom: 'Кастомний'
       }
       return labels[category] || category
     }
  },
  async mounted() {
    await Promise.all([
      this.fetchResults(),
      this.fetchSessions(),
      this.fetchTestTypes()
    ])
    // initCharts() and updateCharts() are removed
  },
  beforeUnmount() {
    // No chart cleanup needed
  }
}
</script>

<style scoped>
.results-page {
  padding: 2rem;
}

.page-header {
  margin-bottom: 2rem;
}

.page-header h1 {
  color: var(--primary-color);
  margin-bottom: 0.5rem;
}

.page-header p {
  color: var(--text-color-secondary);
  margin: 0;
}

.content-section {
  background: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: var(--border-radius);
  padding: 2rem;
  box-shadow: var(--shadow);
  color: var(--text-color);
}

.filters {
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-group label {
  font-weight: 600;
  color: var(--text-color);
}

.filter-dropdown,
.date-picker {
  min-width: 150px;
}

.results-overview {
  margin-bottom: 2rem;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  border: 1px solid var(--surface-border);
  border-radius: 8px;
  background: var(--surface-card);
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
}

.stat-content h3 {
  margin: 0;
  font-size: 2rem;
  color: var(--text-color);
}

.stat-content p {
  margin: 0;
  color: var(--text-color-secondary);
}

.results-tabs {
  margin-top: 2rem;
}

.my-results {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.result-card {
  border: 1px solid var(--surface-border);
  border-radius: 8px;
  padding: 1.5rem;
  transition: all 0.3s ease;
}

.result-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.result-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.result-header h3 {
  margin: 0;
  color: var(--text-color);
  flex: 1;
}

.result-details {
  margin-bottom: 1rem;
}

.result-score {
  display: flex;
  gap: 2rem;
  margin-bottom: 1rem;
}

.score-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.score-label {
  font-size: 0.9rem;
  color: var(--text-color-secondary);
}

.score-value {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--primary-color);
}

.result-actions {
  display: flex;
  gap: 0.5rem;
}

.category-questions {
  margin-top: 1rem;
  padding: 1rem;
  background: var(--surface-ground);
  border-radius: 6px;
}

.question-answer {
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--surface-border);
}

.question-answer:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.question-answer h5 {
  margin: 0 0 0.5rem 0;
  color: var(--text-color);
  font-size: 0.9rem;
  font-weight: 600;
}

.question-answer p {
  margin: 0;
  color: var(--text-color-secondary);
  font-size: 0.9rem;
  line-height: 1.4;
}

.result-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px solid var(--surface-border);
}

.completion-date {
  color: var(--text-color-secondary);
  font-size: 0.9rem;
}

.analytics-section {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.analytics-table h3 {
  margin-bottom: 1rem;
  color: var(--text-color);
}

.empty-state {
  text-align: center;
  padding: 3rem;
  color: var(--text-color-secondary);
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-state h3 {
  margin-bottom: 0.5rem;
  color: var(--text-color);
}

@media (max-width: 768px) {
  .results-page {
    padding: 1rem;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .filters {
    flex-direction: column;
  }
  
  .result-score {
    flex-direction: column;
    gap: 1rem;
  }
  
  .result-footer {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
}
</style> 